'use client';

import {
  IAPIError_getSettings,
  ICustomerCountry,
  IGuidelines,
  IOrderStatusInclAll,
  IPreorderStatusInclAll,
  ISectionName,
  ISettings,
} from 'my-poppies-b2b-types';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { mockApiCallForGuidelines } from '@/api/mockApiCallForGuidelines';
import { mockApiCallForSettings } from '@/api/mockApiCallForSettings';
import { useApi } from '@/api/useApi';
import { AuthContext } from '@/contexts/AuthContext';
import { LanguageContext } from '@/contexts/LanguageContext';
import { useAppPath } from '@/hooks/useAppPath';
import { IFetchState } from '@/types/IFetchState';
import { ISettingsContextProps } from '@/types/ISettingsContextProps';
import { getIsDimmerShown } from '@/utils/getIsDimmerShown';
import { optionsMapping } from '@/utils/optionsMapping';
import { sectionMapping } from '@/utils/sectionMapping';

export const SettingsContext = createContext<ISettingsContextProps>({} as ISettingsContextProps);

export const SettingsContextProvider = ({ children }: PropsWithChildren) => {
  const { callApi } = useApi();
  const { pathname } = useAppPath();
  const { setCurrentLanguage } = useContext(LanguageContext);
  const { userStatus } = useContext(AuthContext);

  const [settings, setSettings] = useState<ISettings | undefined>(undefined);
  const [fetchSettingsState, setFetchSettingsState] = useState<IFetchState>('FETCHING');
  const [guidelines, setGuidelines] = useState<IGuidelines | undefined>(undefined);

  const fetchAndSetSettings = useCallback(async () => {
    if (!getIsDimmerShown()) {
      setFetchSettingsState('FETCHING');
    }
    try {
      const response = await callApi('get', '/settings', {}, mockApiCallForSettings);

      if ((response as IAPIError_getSettings).errorCode) {
        throw new Error('Api Error');
      } else {
        setSettings(response as ISettings);
        setFetchSettingsState('READY');
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e: unknown) {
      setFetchSettingsState('ERROR');
    }
  }, [callApi]);

  // if no user available, but some settings are still there, we need to clean them:
  useEffect(() => {
    if (userStatus === 'NO_USER' && settings) {
      setSettings(undefined);
    }
  }, [settings, userStatus]);

  // if user available, but no settings yet, we need to fetch them:
  useEffect(() => {
    if (userStatus === 'USER_VALID' && !settings) {
      fetchAndSetSettings();
    }
  }, [callApi, fetchAndSetSettings, settings, userStatus]);

  // special case: when entering order new/edit pages, we want to refetch the settings to get newest positions:
  useEffect(() => {
    if (userStatus === 'USER_VALID' && (pathname.startsWith('/orders/new') || pathname.startsWith('/orders/edit'))) {
      fetchAndSetSettings();
    }
  }, [fetchAndSetSettings, pathname, userStatus]);

  const haveSetUserLanguage = useRef<boolean>(false);

  useEffect(() => {
    const userLanguage = settings?.user?.language;
    if (!haveSetUserLanguage.current && userLanguage) {
      haveSetUserLanguage.current = true;
      setCurrentLanguage(userLanguage);
    }
  }, [setCurrentLanguage, settings?.user?.language]);

  const fetchGuidelines = useCallback(async () => {
    const response = await mockApiCallForGuidelines();
    setGuidelines(response);
  }, []);

  const visibleSectionsNamesForUser: ISectionName[] = useMemo(
    () => settings?.visibleSectionsOnRootScreenPerUserRole?.[settings?.user?.role] ?? [],
    [settings?.visibleSectionsOnRootScreenPerUserRole, settings?.user?.role]
  );

  const sectionOrderForUser: FC[] = useMemo(
    () => visibleSectionsNamesForUser.map((section) => sectionMapping[section]).filter(Boolean) as FC[],
    [visibleSectionsNamesForUser]
  );

  const orderStatusOrderForUser: IOrderStatusInclAll[] = useMemo(
    () => settings?.visibleOrderStatusPerUserRole?.[settings?.user?.role] ?? [],
    [settings?.visibleOrderStatusPerUserRole, settings?.user?.role]
  );

  const preorderStatusOrderForUser: IPreorderStatusInclAll[] = useMemo(
    () => settings?.visiblePreorderStatusPerUserRole?.[settings?.user?.role] ?? [],
    [settings?.visiblePreorderStatusPerUserRole, settings?.user?.role]
  );

  const availableCustomerCountriesForCurrentUserRole: ICustomerCountry[] = useMemo(() => {
    if (!settings) {
      return [];
    }

    return settings?.availableCustomerCountriesPerUserRole?.[settings?.user?.role] ?? [];
  }, [settings]);

  const isCurrentUserRoleAllowedToEditOrders: boolean = useMemo(() => {
    if (!settings) {
      return false;
    }

    return settings?.isAllowedToEditOrders?.[settings?.user?.role] ?? false;
  }, [settings]);

  const isCurrentUserRoleAllowedToRedirectShippedOrders: boolean = useMemo(() => {
    if (!settings) {
      return false;
    }

    return settings?.isAllowedToRedirectShippedOrders?.[settings?.user?.role] ?? false;
  }, [settings]);

  const isCurrentUserRoleAllowedToEditPaymentInterval: boolean = useMemo(() => {
    if (!settings) {
      return false;
    }

    return settings?.isAllowedToEditPaymentInterval?.[settings?.user?.role] ?? false;
  }, [settings]);

  const isCurrentUserRoleAllowedToEditBillingType: boolean = useMemo(() => {
    if (!settings) {
      return false;
    }

    return settings?.isAllowedToEditBillingType?.[settings?.user?.role] ?? false;
  }, [settings]);

  const isCurrentUserRoleAllowedToApproveOrDeclineOrders: boolean = useMemo(() => {
    if (!settings) {
      return false;
    }

    return settings?.isAllowedToApproveOrDeclineOrders?.[settings?.user?.role] ?? false;
  }, [settings]);

  const isCurrentUserRoleAllowedToCancelOrders: boolean = useMemo(() => {
    if (!settings) {
      return false;
    }

    return settings?.isAllowedToCancelOrders?.[settings?.user?.role] ?? false;
  }, [settings]);

  const availableUserRolesForNewPositionsForCurrentUser = useMemo(
    () => settings?.availableUserRolesForNewPositionsPerUserRole?.[settings?.user?.role] ?? [],
    [settings?.availableUserRolesForNewPositionsPerUserRole, settings?.user?.role]
  );

  const visibleOptionsForCurrentUserRole = useMemo(
    () => settings?.visibleOptionsPerUserRole?.[settings?.user?.role] ?? [],
    [settings?.user?.role, settings?.visibleOptionsPerUserRole]
  );

  const optionsOrderForUser: FC[] = useMemo(
    () => visibleOptionsForCurrentUserRole.map((section) => optionsMapping[section]).filter(Boolean) as FC[],
    [visibleOptionsForCurrentUserRole]
  );

  const selectSenderShownForCurrentUser = useMemo(
    () => settings?.selectSenderShown?.[settings?.user?.role] ?? false,
    [settings?.selectSenderShown, settings?.user?.role]
  );

  const value: ISettingsContextProps = useMemo((): ISettingsContextProps => {
    return {
      settings,
      setSettings,
      guidelines,
      fetchGuidelines,
      sectionOrderForUser,
      visibleSectionsNamesForUser,
      orderStatusOrderForUser,
      preorderStatusOrderForUser,
      availableCustomerCountriesForCurrentUserRole,
      isCurrentUserRoleAllowedToEditOrders,
      isCurrentUserRoleAllowedToRedirectShippedOrders,
      fetchSettingsState,
      isCurrentUserRoleAllowedToEditPaymentInterval,
      isCurrentUserRoleAllowedToEditBillingType,
      isCurrentUserRoleAllowedToApproveOrDeclineOrders,
      isCurrentUserRoleAllowedToCancelOrders,
      availableUserRolesForNewPositionsForCurrentUser,
      visibleOptionsForCurrentUserRole,
      optionsOrderForUser,
      fetchAndSetSettings,
      selectSenderShownForCurrentUser,
    };
  }, [
    settings,
    guidelines,
    fetchGuidelines,
    sectionOrderForUser,
    visibleSectionsNamesForUser,
    orderStatusOrderForUser,
    preorderStatusOrderForUser,
    availableCustomerCountriesForCurrentUserRole,
    isCurrentUserRoleAllowedToEditOrders,
    isCurrentUserRoleAllowedToRedirectShippedOrders,
    fetchSettingsState,
    isCurrentUserRoleAllowedToEditPaymentInterval,
    isCurrentUserRoleAllowedToEditBillingType,
    isCurrentUserRoleAllowedToApproveOrDeclineOrders,
    availableUserRolesForNewPositionsForCurrentUser,
    isCurrentUserRoleAllowedToCancelOrders,
    visibleOptionsForCurrentUserRole,
    optionsOrderForUser,
    fetchAndSetSettings,
    selectSenderShownForCurrentUser,
  ]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};
